import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faArrowUpFromBracket,
  faEllipsisVertical,
  faSquarePlus,
  faCheck,
  faCircleInfo,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import styles from "./InstallPwaModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { IN_APP_BROWSER_LABELS, logPwaEvent } from "../utils/pwa";

/**
 * Explica, em linguagem de aluno (sem jargão de "PWA", "service worker" ou
 * "standalone"), por que vale instalar o app na tela inicial, e mostra o
 * passo a passo do sistema que a pessoa está usando.
 *
 * O motivo real por trás disso é técnico - o Safari do iPhone só entrega
 * notificação pra sites instalados na Tela de Início - mas o texto aqui
 * traduz isso pro benefício concreto: receber os avisos do treino.
 *
 * Props:
 *   open            – bool
 *   onClose         – () => void
 *   platform        – "ios" | "android" | "other" (de usePwaInstall)
 *   browser         – string (de usePwaInstall) - usado pro caso do
 *                     Chrome/Firefox no iPhone (sem a opção de instalar) E,
 *                     desde R366/R367, pra reconhecer navegador embutido de
 *                     Instagram/Facebook/TikTok/LinkedIn/etc. (ver
 *                     IN_APP_BROWSER_LABELS em utils/pwa.js) e trocar o aviso.
 *   canPromptInstall– bool: Android com prompt nativo disponível
 *   onPromptInstall – () => Promise - abre o diálogo nativo do Android
 */
export default function InstallPwaModal({
  open,
  onClose,
  platform = "other",
  browser = "other",
  canPromptInstall = false,
  onPromptInstall,
}) {
  const [installing, setInstalling] = useState(false);
  useBodyScrollLock(open);

  // R370: um "shown" por abertura - a comparação com app_installed (ver
  // usePwaInstall.js) segmentada por `browser` é o que responde se a baixa
  // adesão vem de navegador embutido ou de outra causa.
  useEffect(() => {
    if (open) logPwaEvent("modal_shown", { platform, browser });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const iosWrongBrowser = platform === "ios" && browser === "ios-other";
  // R366: navegador embutido de rede social/mensageria - tem prioridade
  // sobre o aviso de "Chrome/Firefox no iPhone" acima, é um problema mais
  // específico e mais comum nesse produto (divulgação por link solto).
  const inAppBrowserName = IN_APP_BROWSER_LABELS[browser];

  const handleClose = () => {
    logPwaEvent("modal_closed", { platform, browser });
    onClose();
  };

  const handleNativeInstall = async () => {
    if (!onPromptInstall) return;
    setInstalling(true);
    try {
      await onPromptInstall();
    } finally {
      setInstalling(false);
    }
  };

  const steps = {
    ios: [
      { icon: faArrowUpFromBracket, text: <>Com o app aberto no <strong>Safari</strong>, toque no botão de <strong>Compartilhar</strong> (o quadrado com a seta para cima), na barra inferior.</> },
      { icon: faSquarePlus, text: <>Role a lista e toque em <strong>“Adicionar à Tela de Início”</strong>.</> },
      { icon: faCheck, text: <>Confirme em <strong>“Adicionar”</strong>. Pronto - agora abra o TreinoNexo sempre por esse ícone novo.</> },
      { icon: faBell, text: <>Na primeira vez que abrir pelo ícone, toque em <strong>Ativar avisos</strong> e depois em <strong>Permitir</strong>.</> },
    ],
    android: [
      { icon: faEllipsisVertical, text: <>Toque no menu do navegador (os <strong>três pontinhos</strong>, no canto superior).</> },
      { icon: faSquarePlus, text: <>Escolha <strong>“Instalar app”</strong> ou <strong>“Adicionar à tela inicial”</strong>.</> },
      { icon: faCheck, text: <>Confirme em <strong>“Instalar”</strong>. O ícone do TreinoNexo aparece junto dos seus outros apps.</> },
      { icon: faBell, text: <>Abra pelo ícone, toque em <strong>Ativar avisos</strong> e depois em <strong>Permitir</strong>.</> },
    ],
    other: [
      { icon: faDownload, text: <>Na barra de endereço do navegador, procure o ícone de <strong>instalar</strong> (uma telinha com uma seta) e clique nele.</> },
      { icon: faCheck, text: <>Confirme em <strong>“Instalar”</strong>.</> },
      { icon: faBell, text: <>Abra o app instalado, toque em <strong>Ativar avisos</strong> e depois em <strong>Permitir</strong>.</> },
    ],
  };

  const platformLabel = { ios: "iPhone / iPad", android: "Android", other: "computador" }[platform] || "seu aparelho";
  const currentSteps = steps[platform] || steps.other;

  return createPortal(
    <div className={styles.backdrop} onClick={handleClose} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Instalar o app"
      >
        <ModalHeader onClose={handleClose} />

        <div className={styles.body}>
          <div className={styles.hero}>
            <span className={styles.heroIcon}>
              <FontAwesomeIcon icon={faBell} />
            </span>
            <div>
              <h2 className={styles.title}>Instale o app para receber avisos</h2>
              <p className={styles.subtitle}>
                Você está usando o TreinoNexo pelo navegador.
              </p>
            </div>
          </div>

          <p className={styles.lead}>
            Pelo navegador, o celular <strong>não consegue</strong> te avisar de nada - nem que
            seu treino do dia está esperando, nem quando seu professor manda uma mensagem.
            Instalando o app na tela inicial, esses avisos passam a chegar normalmente,
            como em qualquer outro aplicativo.
          </p>

          <div className={styles.benefits}>
            <strong className={styles.benefitsTitle}>O que melhora ao instalar</strong>
            <ul>
              <li>Lembretes do treino do dia e da sua meta da semana</li>
              <li>Aviso quando seu professor responder ou mandar algo novo</li>
              <li>Abre direto pelo ícone, sem digitar endereço e sem as barras do navegador</li>
              <li>Ocupa pouquíssimo espaço - não é um download da loja de apps</li>
            </ul>
          </div>

          {inAppBrowserName && (
            <div className={styles.warning}>
              <FontAwesomeIcon icon={faCircleInfo} />
              <span>
                Você abriu esse link <strong>dentro do {inAppBrowserName}</strong>. Toque nos{" "}
                <strong>⋯</strong> (ou no ícone de compartilhar) no topo da tela e escolha{" "}
                <strong>"Abrir no navegador"</strong> primeiro - sem isso, o passo a passo abaixo
                não vai aparecer do jeito descrito.
              </span>
            </div>
          )}

          {!inAppBrowserName && iosWrongBrowser && (
            <div className={styles.warning}>
              <FontAwesomeIcon icon={faCircleInfo} />
              <span>
                No iPhone, só o <strong>Safari</strong> consegue instalar o app.
                Abra <strong>treinonexo.com.br</strong> no Safari e siga os passos abaixo.
              </span>
            </div>
          )}

          {/* R366/R367: passos ficam esmaecidos enquanto o aluno está preso no
              navegador embutido - evita que ele siga uma instrução que não bate
              com o que está vendo na tela. Continuam visíveis (não escondidos)
              porque ele pode já ter resolvido e estar só revisitando o modal. */}
          <div className={styles.stepsBlock} style={inAppBrowserName ? { opacity: 0.35 } : undefined}>
            <strong className={styles.stepsTitle}>Como instalar no seu {platformLabel}</strong>
            <ol className={styles.steps}>
              {currentSteps.map((step, i) => (
                <li key={i} className={styles.step}>
                  <span className={styles.stepNum}>{i + 1}</span>
                  <span className={styles.stepIcon}>
                    <FontAwesomeIcon icon={step.icon} />
                  </span>
                  <span className={styles.stepText}>{step.text}</span>
                </li>
              ))}
            </ol>
          </div>

          <p className={styles.footnote}>
            Não se preocupe: seus dados, treinos e histórico continuam exatamente os mesmos.
            É o mesmo TreinoNexo, só que mais fácil de abrir.
          </p>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.btn} onClick={handleClose}>
            Fechar
          </button>
          {canPromptInstall && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleNativeInstall}
              disabled={installing}
            >
              <FontAwesomeIcon icon={faDownload} /> {installing ? "Instalando..." : "Instalar agora"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
